import React, { useEffect, useRef, lazy, createElement } from 'react';
import { CheckCircle, Hammer, Wrench, Package, TrendingUp, Users, Target } from 'lucide-react';
// Declare wistia-player as a valid JSX element
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'wistia-player': any;
    }
  }
}
export function SolutionSection() {
  const features = [{
    title: 'Instrucciones Paso a Paso, de la A a la Z',
    description: 'Recibes proyectos detallados con instrucciones paso a paso que hacen que fabricar muebles plegables sea súper rápido, súper fácil y súper satisfactorio. Es como tener a un MAESTRO CARPINTERO a tu lado, guiándote en cada paso del proyecto entero.'
  }, {
    title: 'Listas de Corte y Materiales EXACTAS',
    description: 'Recibes la lista EXACTA de materiales y cortes para cada proyecto. Vas a poder comprar las cantidades precisas — lo que significa que vas a dejar de tirar tu dinero en madera equivocada, materiales de más o cantidades que no sirven.'
  }, {
    title: 'Esquemas Detallados Con Medidas Exactas',
    description: 'Con esquemas claros y precisos incluidos en cada plano, simplemente NO HAY LUGAR PARA LA ADIVINANZA. El nivel de detalle convierte hasta el proyecto más desafiante en un paseo por el parque.'
  }, {
    title: 'Muebles Específicos Que Se Venden',
    description: 'No son planos genéricos de muebles que nadie quiere. Son diseños de piezas que la gente realmente busca y paga: Cama Murphy, Mesa Picnic Plegable, Escritorios Rebatibles, Estanterías Modulares y más.'
  }, {
    title: 'Apto Para Principiantes Absolutos Y Carpinteros Con Experiencia',
    description: 'Tenemos proyectos que cubren TODOS los niveles de habilidad y competencia. NO necesitas ser un maestro carpintero ni tener maquinaria costosa para usar nuestros planos.'
  }];
  const images = ['https://cdn.shopify.com/s/files/1/1045/1160/3027/files/imagen_2026-02-17_202716747_1.webp?v=1771356510', 'https://cdn.shopify.com/s/files/1/1045/1160/3027/files/image_25_1.webp?v=1771356545', 'https://cdn.shopify.com/s/files/1/1045/1160/3027/files/20260216_2252_Image_Generation_simple_compose_01khm70ttafnesvjy6pjt0m5ps_1.webp?v=1771356406', 'https://cdn.shopify.com/s/files/1/1045/1160/3027/files/20260216_2252_Foldable_Wooden_Patio_Set_simple_compose_01khm71686fqcvja5zmk97erwb_1.webp?v=1771356389', 'https://cdn.shopify.com/s/files/1/1045/1160/3027/files/20260216_2252_Image_Generation_simple_compose_01khm71qwbe5vt754k90wb6jq5.webp?v=1771356365', 'https://cdn.shopify.com/s/files/1/1045/1160/3027/files/20260216_2253_Transforming_Wooden_Chair_simple_compose_01khm71z1vfgxt2308r59q20s7_1.webp?v=1771356335'];
  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Carousel auto-scroll animation
    const scroller = scrollerRef.current;
    if (!scroller) return;
    let animationId: number;
    let scrollPosition = 0;
    const scrollSpeed = 0.5;
    const animate = () => {
      scrollPosition += scrollSpeed;
      if (scrollPosition >= scroller.scrollWidth / 2) {
        scrollPosition = 0;
      }
      scroller.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(animate);
    };
    animationId = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);
  useEffect(() => {
    // Load Wistia scripts
    const playerScript = document.createElement('script');
    playerScript.src = 'https://fast.wistia.com/player.js';
    playerScript.async = true;
    document.head.appendChild(playerScript);
    const embedScript = document.createElement('script');
    embedScript.src = 'https://fast.wistia.com/embed/h21ptcep8k.js';
    embedScript.async = true;
    embedScript.type = 'module';
    document.head.appendChild(embedScript);
    // Add Wistia player styles
    const style = document.createElement('style');
    style.textContent = `
      wistia-player[media-id='h21ptcep8k']:not(:defined) { 
        background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/h21ptcep8k/swatch'); 
        display: block; 
        filter: blur(5px); 
        padding-top: 56.25%; 
      }
    `;
    document.head.appendChild(style);
    return () => {
      // Cleanup scripts on unmount
      if (document.head.contains(playerScript)) {
        document.head.removeChild(playerScript);
      }
      if (document.head.contains(embedScript)) {
        document.head.removeChild(embedScript);
      }
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  const projects = ['Cama Murphy Plegable', 'Mesa Picnic Plegable', 'Escritorio Rebatible de Pared', 'Estantería Modular', 'Mesa de Trabajo Plegable', 'Banco de Jardín', 'Organizador de Herramientas', 'Mueble Bar Plegable', 'Mesa para Balcón', 'Mueble de TV', 'Repisa Flotante', 'Mesa de Centro', 'Silla Plegable Premium', 'Mueble Zapatero', 'Cajonera de Melamina', 'Biblioteca Infantil', 'Mesa de Noche', 'Rack para Bicicletas', 'Mueble Multiuso', 'Carro de Cocina Plegable'];
  return <section className="py-8 md:py-16 px-4 bg-leather bg-blueprint" data-id="element-211">
      <div className="max-w-6xl mx-auto" data-id="element-212">
        {/* Project Types */}
        <div className="mb-8 md:mb-16" data-id="element-213">
          <h3 className="text-2xl md:text-4xl font-black text-[#E4D6B5] text-center mb-6 md:mb-8 uppercase tracking-tight" data-id="element-214">
            Tipos De Proyectos Que Puedes Construir Hoy
          </h3>
          <p className="text-base md:text-xl text-[#C8B894] text-center mb-8 md:mb-12 font-semibold" data-id="element-215">
            Solo una pequeña muestra de lo que incluye el Método:
          </p>

          {/* Image Slider */}
          <div className="mb-6 md:mb-8" data-id="element-216">
            <div className="divider-industrial mb-6 md:mb-8" data-id="element-217"></div>
          </div>

          <div ref={scrollerRef} className="flex gap-4 md:gap-6 overflow-x-hidden mb-6 md:mb-8" style={{
          scrollBehavior: 'auto'
        }} data-id="element-218">
            {[...images, ...images].map((image, index) => <div key={index} className="flex-shrink-0 w-64 md:w-80 lg:w-96 h-64 md:h-80 lg:h-96 bg-[#111111] border-2 md:border-4 border-[#6F6F6F] shadow-[0_4px_0_0_#0a0a0a] md:shadow-[0_8px_0_0_#0a0a0a] overflow-hidden" data-id="element-219">
                <img src={image} alt={`Proyecto de carpintería ${index % images.length + 1}`} className="w-full h-full object-cover" loading="lazy" data-id="element-220" />
              </div>)}
          </div>

          <div className="mb-6 md:mb-8" data-id="element-221">
            <div className="divider-industrial" data-id="element-222"></div>
          </div>

          <p className="text-xl md:text-3xl font-black text-[#8B5A2B] text-center mt-6 md:mt-8 uppercase tracking-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]" data-id="element-223">
            ¡Y Muchos Más!
          </p>
        </div>

        {/* New Achievement Section */}
        <div className="max-w-4xl mx-auto mb-8 md:mb-16" data-id="element-224">
          <div className="prose prose-lg max-w-none" data-id="element-225">
            <p className="text-lg md:text-2xl text-[#E4D6B5] leading-relaxed mb-6 md:mb-8 font-bold text-center" data-id="element-226">
              <span className="font-black uppercase tracking-wide" data-id="element-227">
                ¿QUÉ LOGRARás?
              </span>
            </p>

            {/* Achievement List */}
            <div className="space-y-3 md:space-y-4 mb-8 md:mb-12" data-id="element-228">
              <div className="flex items-start gap-3 md:gap-4 bg-[#111111] p-3 md:p-5 border-l-4 border-[#8B5A2B] shadow-lg" data-id="element-229">
                <CheckCircle className="w-5 md:w-7 h-5 md:h-7 text-[#8B5A2B] flex-shrink-0 mt-1" data-id="element-230" />
                <p className="text-sm md:text-lg text-[#E4D6B5] leading-relaxed font-semibold" data-id="element-231">
                  <span className="text-[#C8B894]" data-id="element-232">
                    Podrás fabricar tus primeros muebles plegables incluso sin
                    experiencia previa y usando herramientas sencillas.
                  </span>
                </p>
              </div>
              <div className="flex items-start gap-3 md:gap-4 bg-[#111111] p-3 md:p-5 border-l-4 border-[#8B5A2B] shadow-lg" data-id="element-233">
                <CheckCircle className="w-5 md:w-7 h-5 md:h-7 text-[#8B5A2B] flex-shrink-0 mt-1" data-id="element-234" />
                <p className="text-sm md:text-lg text-[#E4D6B5] leading-relaxed font-semibold" data-id="element-235">
                  <span className="text-[#C8B894]" data-id="element-236">
                    Evitarás desperdiciar madera y dinero, porque sabrás
                    exactamente qué piezas cortar y cómo hacerlo correctamente.
                  </span>
                </p>
              </div>
              <div className="flex items-start gap-3 md:gap-4 bg-[#111111] p-3 md:p-5 border-l-4 border-[#8B5A2B] shadow-lg" data-id="element-237">
                <CheckCircle className="w-5 md:w-7 h-5 md:h-7 text-[#8B5A2B] flex-shrink-0 mt-1" data-id="element-238" />
                <p className="text-sm md:text-lg text-[#E4D6B5] leading-relaxed font-semibold" data-id="element-239">
                  <span className="text-[#C8B894]" data-id="element-240">
                    Aprenderás a convertir la carpintería en un oficio rentable
                    desde tu casa y empezar a generar ingresos.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Closing Statement */}
        <div className="bg-leather border-4 md:border-8 border-[#8B5A2B] p-6 md:p-10 shadow-[0_0_60px_rgba(139,90,43,0.3)] text-center" data-id="element-241">
          <h3 className="text-2xl md:text-4xl font-black mb-4 md:mb-6 text-[#E4D6B5] uppercase tracking-tight" data-id="element-242">
            Planos Tan Detallados… Que{' '}
            <span className="underline decoration-4 underline-offset-4" data-id="element-243">
              El Mueble
            </span>{' '}
            Prácticamente{' '}
            <span className="underline decoration-4 underline-offset-4" data-id="element-244">
              Se Arma Solo
            </span>
          </h3>
          <p className="text-base md:text-xl leading-relaxed max-w-3xl mx-auto text-[#C8B894] font-semibold" data-id="element-245">
            El Método en Carpintería Plegable hace que estos proyectos no solo
            sean posibles… sino ridículamente simples. No importa cuál sea tu
            nivel actual, tu presupuesto, o si no tienes un taller armado.
          </p>
        </div>

        {/* Wistia Video */}
        <div className="mt-8 md:mt-12 flex flex-col items-center gap-4 md:gap-6" data-id="element-246">
          <h3 className="text-2xl md:text-4xl font-black text-[#E4D6B5] uppercase tracking-tight text-center" data-id="element-247">
            Una Demostración Rápida De Lo Que Recibirás
          </h3>
          <div className="bg-leather border-4 md:border-8 border-[#8B5A2B] p-4 md:p-6 shadow-[0_0_60px_rgba(139,90,43,0.3)] w-full max-w-3xl" data-id="element-248">
            <wistia-player media-id="h21ptcep8k" aspect="1.7777777777777777" data-id="element-249"></wistia-player>
          </div>
        </div>
      </div>
      {/* Main Announcement */}

      <div className="divider-industrial mt-8 md:mt-16" data-id="element-250"></div>
    </section>;
}